// serialize.js — the state <-> wire bridge for Post a Project (CONFIG-SCHEMA §6).
//
// State (camelCase top-level, snake_case posting sub-states) is built by the
// engine; the server speaks Rails nested params (project_posting[...]) and the
// presenter emits camelCase drafts. Conversions happen ONLY at these edges.

import * as OPTIONS from "./options"
import { bundleComposition, bundleOutlinePackage } from "./bundle"

// ── case helpers (shallow) ──────────────────────────────────────────────────

export function snakeCase(str) {
  return String(str)
    .replace(/([a-z0-9])([A-Z])/g, "$1_$2")
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1_$2")
    .toLowerCase()
}

export function camelCase(str) {
  return String(str).replace(/_([a-z0-9])/g, (_, c) => c.toUpperCase())
}

export function snakeKeys(obj) {
  if (!obj || typeof obj !== "object" || Array.isArray(obj)) return obj
  const out = {}
  for (const k of Object.keys(obj)) out[snakeCase(k)] = obj[k]
  return out
}

export function camelKeys(obj) {
  if (!obj || typeof obj !== "object" || Array.isArray(obj)) return obj
  const out = {}
  for (const k of Object.keys(obj)) out[camelCase(k)] = obj[k]
  return out
}

// ── ctx / config walking ────────────────────────────────────────────────────

// serializeForSave is specified as (state, serviceConfigs); ctx is an optional
// third arg so the page can pass the REAL ctx (insider/canUseAi/...). When
// omitted we reconstruct a best-effort ctx so `when` predicates still resolve.
function defaultCtx(state) {
  return {
    insider: false,
    canUseAi: true,
    canPostNarration: true,
    managedTier: state.managed_services_tier,
    selected: state.selected || [],
    options: OPTIONS,
  }
}

function stepsOf(config, subState, ctx) {
  const steps = typeof config.steps === "function" ? config.steps(subState, ctx) : config.steps
  return Array.isArray(steps) ? steps : []
}

// Currently-visible fields for a service (drops `when`-false steps + fields).
function visibleFields(config, subState, ctx) {
  const out = []
  for (const step of stepsOf(config, subState, ctx)) {
    if (step.when && !step.when(subState, ctx)) continue
    for (const field of step.fields || []) {
      if (field.when && !field.when(subState, ctx)) continue
      out.push(field)
    }
  }
  return out
}

// ── composite expansions ────────────────────────────────────────────────────

// consent carrier ("none"|"research"|"all") -> ai_consent_* booleans (§3.1).
// ai_consent_none is ALWAYS present (true or false) so the server's
// none-XOR-others validation resolves.
function expandConsent(uiVal) {
  const out = {
    ai_consent_none: false,
    ai_consent_research: false,
    ai_consent_all: false,
    ai_consent_adjust: false,
    ai_consent_generate: false,
    ai_consent_reduce: false,
    ai_consent_structuring: false,
  }
  if (uiVal === "none") {
    out.ai_consent_none = true
  } else if (uiVal === "research") {
    out.ai_consent_research = true
  } else if (uiVal === "all") {
    out.ai_consent_all = true
    out.ai_consent_adjust = true
    out.ai_consent_generate = true
    out.ai_consent_reduce = true
    out.ai_consent_structuring = true
  }
  return out
}

// The inverse of expandConsent, for resuming a saved draft. A draft round-trips
// through the server as real columns, so a control bound to a synthetic carrier
// reads back unanswered unless the carrier is rebuilt. That is not cosmetic:
// submit re-saves the draft before posting, so a resumed draft with no carrier
// is SAVED as all-seven-false and then rejected (AU-03).
//
// Order matters. "all" persists as five true columns and "research" as one, and
// the two sets do not overlap, so ai_consent_all is the discriminator; an
// explicit "none" is a real answer and outranks both. Anything the three-way
// control cannot express — no answer at all, or a partial column set left by the
// old ERB wizard's seven checkboxes — comes back unanswered rather than being
// rounded to the nearest option. Guessing here would put words in the
// customer's mouth about how their book may be written.
function collapseConsent(attrs) {
  if (attrs.ai_consent_none) return "none"
  if (attrs.ai_consent_all) return "all"
  if (attrs.ai_consent_research) return "research"
  return undefined
}

// The inverse of expandTrigger. Quieter than the consent gap and worse for it:
// nothing rejects a resumed draft whose trigger warnings vanished, so the
// project simply posts without the detail the customer wrote (AU-10).
function collapseTrigger(attrs) {
  if (!attrs.trigger_warning || !attrs.trigger_warning_details) return {}

  try {
    const parsed = JSON.parse(attrs.trigger_warning_details)
    return {
      trigger_warning_warnings: Array.isArray(parsed?.warnings) ? parsed.warnings : [],
      trigger_warning_other: typeof parsed?.other === "string" ? parsed.other : "",
    }
  } catch {
    // The legacy ERB wizard wrote this column as plain prose rather than the
    // JSON envelope, and those drafts still exist. Keep the words as the free
    // -text note instead of discarding them.
    return { trigger_warning_warnings: [], trigger_warning_other: String(attrs.trigger_warning_details) }
  }
}

// Serialization is deliberately asymmetric — expandConsent/expandTrigger fold
// the carriers away on the way out because the raw carrier keys are not
// whitelisted server-side. This folds them back on the way in, so every control
// that binds to a carrier survives a resume.
function restoreCarriers(attrs) {
  const consent = collapseConsent(attrs)
  return { ...attrs, ...(consent ? { ai_consent: consent } : {}), ...collapseTrigger(attrs) }
}

// A single textarea whose lines map to N separate backend columns (SEO's one
// "Competitor URLs" textarea -> comp_url_1/2/3). Splits on newlines, trims,
// drops blank lines, and caps at the number of target keys. The raw textarea key
// is not whitelisted server-side, so it is folded away and never emitted. Every
// target key is written (empty string for an unused slot) so clearing a URL on
// edit clears the column.
function expandSplit(field, raw) {
  const keys = (field.split && field.split.keys) || []
  const lines = String(raw ?? "")
    .split("\n")
    .map((s) => s.trim())
    .filter((s) => s.length > 0)
    .slice(0, keys.length)
  const out = {}
  keys.forEach((k, i) => {
    out[k] = lines[i] ?? ""
  })
  return out
}

// triggerWarning carriers -> trigger_warning + trigger_warning_details JSON
// (§3.2). The raw warnings[]/other carriers are NOT whitelisted, so they are
// folded into the JSON and dropped.
function expandTrigger(subState) {
  const on = !!subState.trigger_warning
  const out = { trigger_warning: on }
  if (on) {
    out.trigger_warning_details = JSON.stringify({
      warnings: Array.isArray(subState.trigger_warning_warnings) ? subState.trigger_warning_warnings : [],
      other: subState.trigger_warning_other || "",
    })
  }
  return out
}

// ── per-service posting serialization ───────────────────────────────────────

function serializePosting(config, subState, ctx) {
  const attrs = {}
  for (const field of visibleFields(config, subState, ctx)) {
    // A field carrying a `split` descriptor fans one UI control out into several
    // backend columns (SEO comp_urls -> comp_url_1/2/3); the raw key is dropped.
    if (field.split) {
      Object.assign(attrs, expandSplit(field, subState[field.key]))
      continue
    }
    switch (field.type) {
      case "consent": {
        Object.assign(attrs, expandConsent(subState[field.key || "ai_consent"]))
        break
      }
      case "triggerWarning": {
        Object.assign(attrs, expandTrigger(subState))
        break
      }
      case "sliders": {
        for (const axis of field.axes || []) {
          if (subState[axis.key] !== undefined) attrs[axis.key] = subState[axis.key]
        }
        break
      }
      case "toggle": {
        attrs[field.key] = !!subState[field.key]
        if (subState[field.key] && field.detailsKey && subState[field.detailsKey] !== undefined) {
          attrs[field.detailsKey] = subState[field.detailsKey]
        }
        break
      }
      case "file": {
        // Carry the real File through so postProjectActions can upload it and
        // send its signed id (the nested key matches the legacy has_one_attached
        // name, so Rails attaches it). Keep a saved {name} marker out of the request;
        // a cleared field must be sent as an empty value so Rails detaches it.
        const f = subState[field.key]
        if (typeof File !== "undefined" && f instanceof File) attrs[field.key] = f
        else if (f === null) attrs[field.key] = ""
        break
      }
      default: {
        if (field.key && subState[field.key] !== undefined) {
          attrs[field.key] = subState[field.key]
        }
      }
    }
  }
  // On Book Writing and Humanize AI's Finance tier an enabled warning stays visible, but an
  // intentional decline hides its control immediately. Carry that explicit
  // false through the save so a previously persisted true is cleared on Rails.
  // A fresh Finance choice with no warning key remains untouched.
  if (["book_writing", "humanize_ai"].includes(config.key) && subState.tier === "Finance and Technology") {
    if (subState.nsfw === false && Object.prototype.hasOwnProperty.call(subState, "nsfw")) attrs.nsfw = false
    if (subState.trigger_warning === false && Object.prototype.hasOwnProperty.call(subState, "trigger_warning")) {
      attrs.trigger_warning = false
    }
  }
  return attrs
}

// managed_service_details only carries meaning under Managed; waivers submit
// "1"/"0" (must not be "0" to count as agreed).
function serializeManagedDetails(state) {
  if ((state.managed_services_tier || "self_managed") !== "managed") return {}
  const d = state.managed_service_details || {}
  return {
    freelancer_selection: d.freelancer_selection,
    freelancer_note: d.freelancer_note,
    pre_scheduling: d.pre_scheduling,
    progress_updates: d.progress_updates,
    zoom_call: d.zoom_call,
    additional_services: d.additional_services,
    quality_waiver: d.quality_waiver ? "1" : "0",
    timeframe_waiver: d.timeframe_waiver ? "1" : "0",
  }
}

// The JobPosting `step` enum anchor. The real wizard cursor lives in
// current_step_api; `step` is only a coherent server-side autosave anchor, so we
// pin a validation-light in-progress step. (FLAG: confirm the desired enum
// mapping with the backend agent — see report.)
function stepAnchor(state) {
  if (state.stage === "posted") return "complete__one"
  return "package_selection__two"
}

// ── public API ──────────────────────────────────────────────────────────────

// The persisted wizard cursor. `stage/queueIndex/stepIndex/selected` locate the
// draft; `stamp` (stepLabel/stepNum/stepTotal) is the resolved human position,
// computed by the wizard from its OWN visible-step model (getServiceSteps) and
// stamped here so the dashboard resume banner reports a faithful per-step
// position without re-deriving the client-only step tree server-side (Addendum
// A / CR-001). The stamp is additive: absent on legacy rows saved before this
// change, where the presenter falls back to its coarse phase projection.
export function currentStepApi(state = {}, stamp = null) {
  const cursor = {
    version: 1,
    stage: state.stage ?? "entry",
    queueIndex: state.queueIndex ?? 0,
    stepIndex: state.stepIndex ?? 0,
    selected: state.selected ?? [],
  }
  if (stamp && stamp.stepTotal) {
    cursor.stepLabel = stamp.stepLabel
    cursor.stepNum = stamp.stepNum
    cursor.stepTotal = stamp.stepTotal
  }
  return cursor
}

export function serializeForSave(state = {}, serviceConfigs = {}, ctx = null, stamp = null) {
  const useCtx = ctx || defaultCtx(state)

  const pp = {
    project_name: state.projectName || "",
    bundle: !!state.bundle,
    bundle_tier: state.bundleTier ?? null,
    step: stepAnchor(state),
    current_step_api: currentStepApi(state, stamp),
    managed_services_tier: state.managed_services_tier ?? "self_managed",
    quality_assurance_addon: state.quality_assurance_addon ?? null,
    quality_control_enabled: state.quality_control_enabled ?? null,
    managed_service_details: serializeManagedDetails(state),
  }

  for (const key of state.selected || []) {
    const config = serviceConfigs[key]
    if (!config) continue
    pp[key] = true
    const subState = (state.postings && state.postings[key]) || {}
    const groupKey = `${config.postingKey || `${key}_posting`}_attributes`
    pp[groupKey] = serializePosting(config, subState, useCtx)
  }

  return { project_posting: pp }
}

// ── bundle serialization ──────────────────────────────────────────────────────
//
// A bundle persists into the SAME nested *_posting_attributes, but the wizard
// only configures book_writing + cover_design directly; outline / narration /
// description / formatting are bundle-derived. We reuse serializePosting for the
// two configured children (so consent/trigger/slider composites still expand),
// then inject the bundle-specific bits:
//   - book_writing: force fiction (Ultimate Fiction), use_ai, add_outline=false —
//     the three __one/__two/__three booleans submit needs, even when their UI is
//     hidden (the bundle owns its outline; the inline one is suppressed).
//   - cover_design: inherit title/subtitle/author_name/niche from the manuscript
//     (cover_design__two skips them for bundles; create_order copies from parent).
//   - outline child (Platinum/Narration only): package_type + collaborative.
//   - narration child (Narration + fiction): character_voice_count + word_count.
//   - the 3 optional requirement uploads on the parent (bundle_*_file).
// remove_postings no-ops for bundles, so the service booleans (set by make_bundle!)
// don't need re-sending.
export function serializeBundleForSave(state = {}, serviceConfigs = {}, ctx = null, stamp = null) {
  const tier = state.bundleTier
  const comp = bundleComposition(tier)
  const useCtx = ctx || { ...defaultCtx(state), bundle: true, bundleIncludesOutline: !!comp.outline }

  const bw = (state.postings && state.postings.book_writing) || {}
  const cover = (state.postings && state.postings.cover_design) || {}
  const extras = state.bundleExtras || {}

  const pp = {
    project_name: state.projectName || "",
    bundle: true,
    bundle_tier: tier ?? null,
    step: stepAnchor(state),
    current_step_api: currentStepApi(state, stamp),
    managed_services_tier: state.managed_services_tier ?? "self_managed",
    quality_assurance_addon: state.quality_assurance_addon ?? null,
    quality_control_enabled: state.quality_control_enabled ?? null,
    managed_service_details: serializeManagedDetails(state),
  }

  // book_writing — reuse the config (composites expand), then pin the booleans.
  const bwConfig = serviceConfigs.book_writing
  const bwAttrs = bwConfig ? serializePosting(bwConfig, bw, useCtx) : {}
  bwAttrs.fiction = comp.forceFiction ? true : !!bw.fiction
  bwAttrs.use_ai = !!bw.use_ai
  bwAttrs.add_outline = false
  // A cleared upload whose field is hidden (the Platinum upgrade drops a saved
  // bring-your-own outline) still sends the empty value that detaches it.
  if (bw.file === null) bwAttrs.file = ""
  pp.book_writing_posting_attributes = bwAttrs

  // cover_design — reuse the config, inherit the manuscript identity fields.
  const coverConfig = serviceConfigs.cover_design
  const coverAttrs = coverConfig ? serializePosting(coverConfig, cover, useCtx) : {}
  if (bw.title !== undefined) coverAttrs.title = bw.title
  if (bw.subtitle !== undefined) coverAttrs.subtitle = bw.subtitle
  if (bw.author_name !== undefined) coverAttrs.author_name = bw.author_name
  if (!bwAttrs.fiction && bw.niche !== undefined) coverAttrs.niche = bw.niche
  pp.cover_design_posting_attributes = coverAttrs

  // outline child — Platinum / Narration only (Fiction's plot outline is order-time).
  // The flag is what make_bundle! sets; sending it keeps a Silver/Gold draft
  // upgraded in the outline step identical to one launched as Platinum.
  if (comp.outlineChild) {
    pp.outline = true
    pp.outline_posting_attributes = {
      package_type: bundleOutlinePackage(tier),
      collaborative: extras.collaborative === false ? false : true,
    }
  }

  // narration child — Narration bundle; character_voice_count required for fiction.
  if (comp.narration) {
    const narration = {}
    if (bw.word_count !== undefined) narration.word_count = bw.word_count
    if (bwAttrs.fiction && extras.character_voice_count !== undefined) {
      narration.character_voice_count = extras.character_voice_count
    }
    pp.narration_posting_attributes = narration
  }

  // Optional requirement uploads: keep saved markers, replace Files, clear nulls.
  const files = extras.files || {}
  for (const slot of ["cover", "description", "narration"]) {
    const value = files[slot]
    if (typeof File !== "undefined" && value instanceof File) pp[`bundle_${slot}_file`] = value
    else if (value === null) pp[`bundle_${slot}_file`] = ""
  }

  return { project_posting: pp }
}

export function hydrateFromDraft(draft, serviceConfigs = {}) {
  if (!draft) return null

  const knownKeys = new Set(Object.keys(serviceConfigs))
  const known = (k) => knownKeys.size === 0 || knownKeys.has(k)
  const services = draft.services || {}
  const flagged = Object.keys(services)
    .filter((k) => services[k] && k !== "paperbackFormatting")
    .map(snakeCase)
    .filter(known)

  // On the BYO path the persisted cursor's `selected` is the customer's own
  // selection (AT-04 ownership) and outranks the service flags. The Book
  // Writing outline add-on flips the draft's `outline` flag true without the
  // customer ever picking the Outline service; rebuilding the selection from
  // the flags turned that add-on into a "selected" standalone Outline, and the
  // next autosave wrote it back into the cursor, so the server could no longer
  // tell a declined add-on from an outline the customer chose (UPDA-88). The
  // flags remain the source for bundles (the bundle owns its services) and for
  // drafts saved before the cursor existed.
  const cursorSelected = Array.isArray(draft.currentStepApi?.selected)
    ? draft.currentStepApi.selected.map(snakeCase).filter(known).filter((k) => flagged.includes(k))
    : []
  const selected = !draft.bundle && cursorSelected.length ? cursorSelected : flagged

  const draftPostings = draft.postings || {}
  const postings = {}
  for (const key of selected) {
    const camelPostingKey = `${camelCase(key)}Posting` // book_writing -> bookWritingPosting
    const raw = draftPostings[camelPostingKey]
    postings[key] = raw ? restoreCarriers(snakeKeys(raw)) : {}
  }

  // managed_service_details: keys are already snake; coerce waivers/booleans for the UI.
  const msd = snakeKeys(draft.managedServiceDetails || {})
  for (const wk of ["quality_waiver", "timeframe_waiver"]) {
    if (msd[wk] !== undefined) msd[wk] = msd[wk] === true || msd[wk] === "1"
  }
  for (const bk of ["pre_scheduling", "additional_services"]) {
    if (msd[bk] === "true") msd[bk] = true
    else if (msd[bk] === "false") msd[bk] = false
  }

  // Bundle-only sub-state: the outline collaboration choice + narration voice count
  // live on the derived children, not the configured ones.
  const outlineRaw = snakeKeys(draftPostings.outlinePosting || {})
  const narrationRaw = snakeKeys(draftPostings.narrationPosting || {})
  const bundleExtras = {
    collaborative: outlineRaw.collaborative === undefined ? true : !!outlineRaw.collaborative,
    character_voice_count: narrationRaw.character_voice_count ?? null,
    files: draft.bundleFiles || {},
  }

  return {
    draftId: draft.id ?? null,
    projectName: draft.projectName ?? "",
    path: draft.bundle ? "bundle" : "byo",
    bundle: !!draft.bundle,
    bundleTier: draft.bundleTier ?? null,
    selected,
    stage: "entry",
    queueIndex: 0,
    stepIndex: 0,
    postings,
    bundleExtras,
    managed_services_tier: draft.managedServicesTier ?? "self_managed",
    managed_service_details: msd,
    // QA/QC are opt-in; default to an explicit "declined" so submit's
    // quality_assurance__one (required for QA-eligible work) passes untouched.
    quality_assurance_addon: draft.qualityAssuranceAddon ?? "declined",
    quality_control_enabled: draft.qualityControlEnabled ?? "declined",
    discount: null,
    serverCost: null,
  }
}
